import React, { useState, useEffect } from 'react';
import { 
  HelpCircle, 
  Lightbulb,
  Users, 
  Zap,
  TrendingUp,
  Heart,
  Globe,
  Rocket,
  RefreshCw,
  Calendar,
  Mail,
  ArrowRight,
  CheckCircle,
  Send,
  X,
  Info,
  Target,
  Cpu,
  Briefcase,
  Leaf,
  Sparkles,
  MessageCircle,
  Eye,
  Settings,
  FileText,
  Book,
  Phone
} from 'lucide-react';

// Import components from main site
import HeaderNav from './HeaderNav';
import FooterLan from './FooterLan';

// Brand colors aligned with main site
const COLORS = {
  primary: '#ce2223',
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',
  primaryLighter: '#fef2f2',
  secondary: '#fdb52a',
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb',
  black: '#111827',
  darkGray: '#374151',
  mediumGray: '#6b7280',
  lightGray: '#e5e7eb',
  offWhite: '#f9fafb',
  white: '#ffffff',
  success: '#10b981',
  successLight: '#ecfdf5'
};

// Question categories for different innovation perspectives
const QUESTION_CATEGORIES = {
  userCentered: {
    name: 'Customer-Focused',
    focus: 'User Needs & Experience',
    description: 'Questions that put customer needs and experiences at the center',
    color: 'blue',
    bgClass: 'bg-blue-50',
    borderClass: 'border-blue-200',
    textClass: 'text-blue-800',
    iconClass: 'text-blue-600',
    iconBg: 'bg-blue-100',
    icon: Users
  },
  technology: {
    name: 'Technology-Driven',
    focus: 'Digital Solutions & Tools',
    description: 'Explore technical solutions and digital innovation opportunities',
    color: 'purple',
    bgClass: 'bg-purple-50',
    borderClass: 'border-purple-200',
    textClass: 'text-purple-800',
    iconClass: 'text-purple-600',
    iconBg: 'bg-purple-100',
    icon: Cpu
  },
  business: {
    name: 'Business Strategy',
    focus: 'Operations & Revenue',
    description: 'Business model, partnership, and operational improvement questions',
    color: 'green',
    bgClass: 'bg-green-50',
    borderClass: 'border-green-200',
    textClass: 'text-green-800',
    iconClass: 'text-green-600',
    iconBg: 'bg-green-100',
    icon: Briefcase
  },
  experience: {
    name: 'Experience Design',
    focus: 'Service & Interactions',
    description: 'Questions about touchpoints, service design, and user interactions',
    color: 'orange',
    bgClass: 'bg-orange-50',
    borderClass: 'border-orange-200',
    textClass: 'text-orange-800',
    iconClass: 'text-orange-600',
    iconBg: 'bg-orange-100',
    icon: Heart
  },
  sustainability: {
    name: 'Sustainability',
    focus: 'Environmental & Social Impact',
    description: 'Questions about environmental responsibility and social impact',
    color: 'emerald',
    bgClass: 'bg-emerald-50',
    borderClass: 'border-emerald-200',
    textClass: 'text-emerald-800',
    iconClass: 'text-emerald-600',
    iconBg: 'bg-emerald-100',
    icon: Leaf
  },
  disruptive: {
    name: 'Bold Innovation',
    focus: 'Breakthrough Ideas',
    description: 'Questions that challenge assumptions and explore radical alternatives',
    color: 'red',
    bgClass: 'bg-red-50',
    borderClass: 'border-red-200',
    textClass: 'text-red-800',
    iconClass: 'text-red-600',
    iconBg: 'bg-red-100',
    icon: Rocket
  }
};

// Example business problems
const EXAMPLE_PROBLEMS = [
  {
    text: "Our customers find our checkout process too complicated and abandon their purchases",
    icon: <Users className="h-4 w-4" />,
    color: "bg-blue-50 text-blue-700 border-blue-200"
  },
  {
    text: "Remote employees feel disconnected from company culture and team collaboration",
    icon: <MessageCircle className="h-4 w-4" />,
    color: "bg-green-50 text-green-700 border-green-200"
  },
  {
    text: "Our product development process is slower than our competitors",
    icon: <Zap className="h-4 w-4" />,
    color: "bg-purple-50 text-purple-700 border-purple-200"
  },
  {
    text: "Customers don't understand the value of our premium features",
    icon: <Eye className="h-4 w-4" />,
    color: "bg-orange-50 text-orange-700 border-orange-200"
  },
  {
    text: "Our business operations have significant environmental impact",
    icon: <Leaf className="h-4 w-4" />,
    color: "bg-emerald-50 text-emerald-700 border-emerald-200"
  },
  {
    text: "We're losing market share to newer, more innovative companies",
    icon: <Target className="h-4 w-4" />,
    color: "bg-red-50 text-red-700 border-red-200"
  }
];

// Enhanced Button component
const Button = ({ 
  children, 
  onClick, 
  isLoading = false, 
  fullWidth = false, 
  variant = 'primary', 
  icon, 
  className = '', 
  disabled = false,
  size = 'md'
}) => {
  const baseStyle = "font-medium transition-all duration-300 flex items-center justify-center shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500";
  
  const sizes = {
    sm: "py-2 px-4 text-sm rounded-md",
    md: "py-3 px-6 text-base rounded-lg",
    lg: "py-4 px-8 text-lg rounded-xl"
  };
  
  const variants = {
    primary: `bg-red-600 hover:bg-red-700 text-white ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`,
    outline: `bg-white border-2 border-red-600 text-red-600 hover:bg-red-50 ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`,
    secondary: `bg-gray-100 hover:bg-gray-200 text-gray-700 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
  };

  return (
    <button
      onClick={onClick}
      disabled={isLoading || disabled}
      className={`${baseStyle} ${sizes[size]} ${variants[variant]} ${fullWidth ? 'w-full' : ''} ${className}`}
    >
      {isLoading ? (
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
      ) : (
        icon && <span className="mr-2">{icon}</span>
      )}
      {children}
    </button>
  );
};

// Toast notification with better mobile support
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 5000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const styles = {
    success: 'bg-green-50 text-green-800 border-green-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200'
  };
  
  return (
    <div className={`fixed top-20 left-4 right-4 sm:right-4 sm:left-auto sm:max-w-sm z-50 px-4 py-3 rounded-lg shadow-lg border flex items-center gap-3 ${styles[type]}`}>
      <CheckCircle className="h-5 w-5 flex-shrink-0" />
      <p className="font-medium flex-1">{message}</p>
      <button 
        onClick={onClose} 
        className="flex-shrink-0 p-1 hover:bg-black/10 rounded"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// Question Card Component with mobile improvements
const QuestionCard = ({ category, questions, index }) => {
  const cat = QUESTION_CATEGORIES[category];
  const CategoryIcon = cat.icon;
  
  return (
    <article className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-12 h-12 rounded-lg ${cat.iconBg} flex items-center justify-center flex-shrink-0`}>
            <CategoryIcon className={`h-6 w-6 ${cat.iconClass}`} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-lg text-gray-900 leading-tight">{cat.name}</h3>
            <p className="text-sm text-gray-600 font-medium">{cat.focus}</p>
          </div>
        </div>
        
        {/* Description */}
        <p className="text-sm text-gray-600 mb-6 leading-relaxed">{cat.description}</p>
        
        {/* Questions */}
        <div className="space-y-4">
          {questions.hmw && questions.hmw.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-red-600" />
                How Might We...
              </h4>
              <div className="space-y-2">
                {questions.hmw.map((question, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <p className="text-sm text-gray-800 leading-relaxed">{question}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {questions.whatif && questions.whatif.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-red-600" />
                What If...
              </h4>
              <div className="space-y-2">
                {questions.whatif.map((question, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <p className="text-sm text-gray-800 leading-relaxed">{question}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

// Loading animation with better mobile support
const LoadingAnimation = () => {
  const [currentCategory, setCurrentCategory] = useState(0);
  const categoryKeys = Object.keys(QUESTION_CATEGORIES);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCategory(prev => (prev + 1) % categoryKeys.length);
    }, 700);
    return () => clearInterval(interval);
  }, [categoryKeys.length]);
  
  const currentCat = QUESTION_CATEGORIES[categoryKeys[currentCategory]];
  const CurrentIcon = currentCat.icon;
  
  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 sm:p-8 max-w-md mx-auto text-center">
      <div className="w-20 h-20 mx-auto mb-6 relative">
        <div className={`w-full h-full rounded-xl ${currentCat.bgClass} border-2 ${currentCat.borderClass} flex items-center justify-center transition-all duration-500`}>
          <CurrentIcon className={`h-8 w-8 ${currentCat.iconClass}`} />
        </div>
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 rounded-full animate-pulse flex items-center justify-center">
          <HelpCircle className="h-3 w-3 text-white" />
        </div>
      </div>
      
      <h3 className="text-xl font-bold text-gray-900 mb-2">Creating Question Bank</h3>
      <div className="flex items-center justify-center gap-2 text-red-600 mb-4">
        <Lightbulb className="h-5 w-5" />
        <span className="font-medium">Exploring {currentCat.name} perspective...</span>
      </div>
      <p className="text-gray-600 text-sm leading-relaxed">
        This takes about 30 seconds while we generate How Might We and What If questions from multiple innovation perspectives.
      </p>
    </div>
  );
};

// Email capture with mobile improvements
const EmailCapture = ({ onSubmit, isSubmitting }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  
  const handleSubmit = () => {
    if (!email || !email.includes('@')) return;
    onSubmit({ email, name });
  };
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-3">
          <Book className="h-6 w-6 text-red-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Get Complete Question Bank</h3>
        <p className="text-gray-600 text-sm">Receive all questions in PDF format with workshop facilitation guide and templates</p>
      </div>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="name-input" className="block text-sm font-medium text-gray-700 mb-2">
            Name
          </label>
          <input
            id="name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
          />
        </div>
        
        <div>
          <label htmlFor="email-input" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            id="email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
          />
        </div>
        
        <Button
          onClick={handleSubmit}
          disabled={!email || !email.includes('@')}
          isLoading={isSubmitting}
          icon={<Send className="h-4 w-4" />}
          fullWidth
        >
          Send Question Bank
        </Button>
      </div>
      
      <p className="text-xs text-gray-500 text-center mt-4">
        Includes workshop templates and facilitation tips for team sessions
      </p>
    </div>
  );
};

// Success message
const SubmissionSuccess = ({ email }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 sm:p-8 text-center max-w-md mx-auto">
      <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
        <CheckCircle className="h-8 w-8 text-green-600" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">Question Bank Sent Successfully!</h3>
      <p className="text-gray-600 mb-4">
        Check your email at <span className="font-semibold text-gray-900">{email}</span> for your complete creative question collection with facilitation guide.
      </p>
      <p className="text-gray-600 text-sm">
        Our design thinking experts will follow up to help you run effective innovation workshops with your team.
      </p>
    </div>
  );
};

// Example problem card with better mobile design
const ExampleCard = ({ problem, onClick }) => {
  return (
    <button
      onClick={() => onClick(problem.text)}
      className="p-4 bg-white border border-gray-200 rounded-lg text-left hover:border-red-300 hover:shadow-sm transition-all w-full group focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-8 h-8 rounded-lg border ${problem.color} flex items-center justify-center`}>
          {problem.icon}
        </div>
        <div className="text-xs text-gray-500 group-hover:text-red-600 transition-colors font-medium">
          Try this example →
        </div>
      </div>
      <p className="text-gray-800 font-medium text-sm leading-relaxed">
        {problem.text}
      </p>
    </button>
  );
};

// Main component
const HowMightWeWhatIfGenerator = () => {
  const [problem, setProblem] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [questions, setQuestions] = useState({});
  const [toast, setToast] = useState(null);
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  
  const generateQuestions = async () => {
    if (!problem.trim()) {
      setToast({ message: 'Please describe your problem or challenge first', type: 'error' });
      return;
    }
    
    setIsLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    try {
      const response = await fetch('https://proxyapi-te6hpgqhgq-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-3-5-sonnet-20241022',
          max_tokens: 4000,
          temperature: 0.8,
          messages: [{
            role: 'user',
            content: `Based on this business problem: "${problem}"

Generate creative "How Might We" and "What If" questions from 6 different innovation perspectives:

1. CUSTOMER-FOCUSED: Focus on customer needs, pain points, and user experience
2. TECHNOLOGY-DRIVEN: Explore tech solutions, digital innovation, and emerging technologies  
3. BUSINESS STRATEGY: Revenue models, partnerships, operations, and strategy
4. EXPERIENCE DESIGN: Service design, touchpoints, interactions, and journey mapping
5. SUSTAINABILITY: Environmental impact, social responsibility, and sustainable practices
6. BOLD INNOVATION: Breakthrough ideas, paradigm shifts, and revolutionary approaches

For each category, provide:
- 2 "How Might We" questions that reframe the problem as opportunities
- 2 "What If" questions that explore possibilities and scenarios

Make questions specific, actionable, and thought-provoking. Keep language simple and business-focused.

Respond in this JSON format:
{
  "questions": {
    "userCentered": {
      "hmw": ["How might we question 1", "How might we question 2"],
      "whatif": ["What if scenario 1", "What if scenario 2"]
    },
    "technology": {
      "hmw": ["How might we question 1", "How might we question 2"],
      "whatif": ["What if scenario 1", "What if scenario 2"]
    },
    "business": {
      "hmw": ["How might we question 1", "How might we question 2"],
      "whatif": ["What if scenario 1", "What if scenario 2"]
    },
    "experience": {
      "hmw": ["How might we question 1", "How might we question 2"],
      "whatif": ["What if scenario 1", "What if scenario 2"]
    },
    "sustainability": {
      "hmw": ["How might we question 1", "How might we question 2"],
      "whatif": ["What if scenario 1", "What if scenario 2"]
    },
    "disruptive": {
      "hmw": ["How might we question 1", "How might we question 2"],
      "whatif": ["What if scenario 1", "What if scenario 2"]
    }
  }
}`
          }]
        })
      });

      if (!response.ok) throw new Error('Failed to generate questions');
      
      const data = await response.json();
      const jsonMatch = data.content[0].text.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const questionsData = JSON.parse(jsonMatch[0]);
        setTimeout(() => {
          setQuestions(questionsData.questions || {});
          setIsLoading(false);
        }, 2500);
      } else {
        throw new Error('Could not parse response');
      }
    } catch (error) {
      console.error('Error:', error);
      setIsLoading(false);
      setToast({ message: 'Unable to generate questions. Please try again.', type: 'error' });
    }
  };
  
  const handleEmailSubmit = (data) => {
    setIsEmailSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsEmailSubmitting(false);
      setEmailSubmitted(true);
      setSubmittedEmail(data.email);
      setToast({ message: 'Question bank sent to your email successfully!', type: 'success' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1500);
  };
  
  const handleReset = () => {
    setProblem('');
    setQuestions({});
    setEmailSubmitted(false);
    setSubmittedEmail('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleSelectExample = (text) => {
    setProblem(text);
    const textarea = document.getElementById('problem-input');
    if (textarea) {
      setTimeout(() => {
        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
        textarea.focus();
      }, 100);
    }
  };
  
  const hasQuestions = Object.keys(questions).length > 0;

  // SEO metadata (would be handled by helmet in real implementation)
  useEffect(() => {
    document.title = "How Might We + What If Generator - Creative Problem Solving Questions | Lusidea";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Transform business problems into creative opportunities. Generate How Might We and What If questions from multiple innovation perspectives for effective brainstorming and problem-solving.');
    }
  }, []);
  
  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderNav />
      
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Header Section */}
      <header className="bg-white pt-24 sm:pt-28 pb-12 sm:pb-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-2xl flex items-center justify-center mb-6">
            <HelpCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            How Might We + What If Generator
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Transform any business problem into creative questions that spark innovation and breakthrough thinking
          </p>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        
        {/* Input Section */}
        {!isLoading && !hasQuestions && !emailSubmitted && (
          <div className="space-y-8">
            
            {/* Main Input */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                What problem or challenge would you like to explore?
              </h2>
              <div className="mb-6">
                <label htmlFor="problem-input" className="block text-sm font-medium text-gray-700 mb-3">
                  Describe your business problem or challenge
                </label>
                <textarea
                  id="problem-input"
                  value={problem}
                  onChange={(e) => setProblem(e.target.value)}
                  placeholder="Describe your problem or challenge... For example: 'Our customers find our checkout process too complicated and abandon their purchases' or 'Remote employees feel disconnected from company culture and team collaboration'"
                  className="w-full p-4 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none min-h-32 text-gray-800 resize-y"
                  rows={6}
                />
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-3 gap-2">
                  <span className="text-sm text-gray-500">Be specific about your problem to get more targeted questions</span>
                  <span className="text-sm text-gray-400">{problem.length} characters</span>
                </div>
              </div>
              <div className="text-center">
                <Button
                  onClick={generateQuestions}
                  disabled={!problem.trim()}
                  icon={<HelpCircle className="h-5 w-5" />}
                  size="lg"
                >
                  Generate Creative Questions
                </Button>
              </div>
            </section>
            
            {/* Example Problems */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">
                Not sure where to start? Try one of these example problems:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {EXAMPLE_PROBLEMS.map((problem, idx) => (
                  <ExampleCard
                    key={idx}
                    problem={problem}
                    onClick={handleSelectExample}
                  />
                ))}
              </div>
            </section>
            
            {/* How it works */}
            <section className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <Info className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-3">How this tool works</h3>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    This tool uses proven design thinking methods to reframe problems as opportunities. 
                    "How Might We" questions help find practical solutions, while "What If" scenarios explore bold possibilities. 
                    Questions are generated from 6 different innovation perspectives to ensure comprehensive exploration.
                  </p>
                </div>
              </div>
            </section>
          </div>
        )}
        
        {/* Loading State */}
        {isLoading && <LoadingAnimation />}
        
        {/* Results Section */}
        {!isLoading && hasQuestions && !emailSubmitted && (
          <div className="space-y-8">
            {/* Results Header */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Your Creative Question Bank</h2>
                  <p className="text-gray-600 text-sm">
                    Problem: <span className="font-medium">"{problem.slice(0, 100)}..."</span>
                  </p>
                </div>
              </div>
            </section>

            {/* Innovation Perspectives Legend */}
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4 text-center">Innovation Perspectives Used</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {Object.entries(QUESTION_CATEGORIES).map(([key, category]) => {
                  const CategoryIcon = category.icon;
                  return (
                    <div key={key} className="flex flex-col items-center text-center p-3 rounded-lg bg-gray-50">
                      <div className={`w-10 h-10 rounded-lg ${category.iconBg} flex items-center justify-center mb-2`}>
                        <CategoryIcon className={`h-5 w-5 ${category.iconClass}`} />
                      </div>
                      <span className="text-xs text-gray-700 font-medium">{category.name}</span>
                    </div>
                  );
                })}
              </div>
            </section>
            
            {/* Question Cards */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {Object.entries(QUESTION_CATEGORIES).map(([categoryKey, _]) => (
                <QuestionCard
                  key={categoryKey}
                  category={categoryKey}
                  questions={questions[categoryKey] || {}}
                  index={0}
                />
              ))}
            </section>

            {/* Email Capture */}
            <section className="max-w-2xl mx-auto">
              <EmailCapture onSubmit={handleEmailSubmit} isSubmitting={isEmailSubmitting} />
            </section>

            {/* Workshop CTA */}
            <section className="bg-gradient-to-r from-red-50 to-red-100 rounded-xl border border-red-200 p-6 sm:p-8 text-center">
              <div className="max-w-3xl mx-auto">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Need Workshop Facilitation?</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Get expert facilitation for design thinking workshops using these questions with your team. 
                  Our experts will help you run productive innovation sessions and turn ideas into action.
                </p>
                <a
                  href="https://calendly.com/lusidea/design-thinking-workshop"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 py-3 px-6 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 shadow-sm"
                >
                  <Calendar className="h-5 w-5" />
                  Schedule Workshop Session
                </a>
              </div>
            </section>
            
            {/* Reset Action */}
            <div className="text-center pt-4">
              <Button
                onClick={handleReset}
                variant="outline"
                icon={<RefreshCw className="h-4 w-4" />}
              >
                Explore Another Problem
              </Button>
            </div>
          </div>
        )}
        
        {/* Success State */}
        {emailSubmitted && <SubmissionSuccess email={submittedEmail} />}
      </main>
      
      <FooterLan />
      
      <style jsx global>{`
        /* Enhanced focus states for accessibility */
        input:focus, textarea:focus {
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }
        
        /* Smooth transitions */
        * {
          transition-property: background-color, border-color, color, fill, stroke, opacity, box-shadow, transform;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 150ms;
        }
        
        @media (max-width: 640px) {
          .line-clamp-3 {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        }
      `}</style>
    </div>
  );
};

export default HowMightWeWhatIfGenerator;